import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash-es';
import { useMemo } from 'react';
import remoteDbService from '@/core/database/RemoteDBService';
import {
  type Collection,
  type DisplayedCollectionSection,
} from '@/features/collections/types/collection.types';
import { type Page, type PageMetadata } from '@/features/pages/types/page.types';
import { publicKeys } from '@/shared/query/queryKeys';

export const useOptimizedCollectionNotes = (
  collection: Collection | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  // Stage 1: Fast metadata query for sidebar structure
  const { data: pagesMetadata, isLoading: isMetadataLoading } = useQuery<PageMetadata[]>({
    queryKey: publicKeys.collectionNotesMetadata(collection?.id),
    queryFn: async () => {
      if (!collection?.id) throw new Error('Collection ID is required');
      return await remoteDbService.getCollectionNotesMetadata(collection.id);
    },
    enabled: enabled && !!collection?.id,
  });

  // Stage 2: Background full content query
  const { data: fullPages, isLoading: isContentLoading } = useQuery<Page[]>({
    queryKey: publicKeys.collectionNotesFull(collection?.id),
    queryFn: async () => {
      if (!collection?.id) throw new Error('Collection ID is required');
      return await remoteDbService.getCollectionNotes(collection.id);
    },
    // Only start after metadata is loaded
    enabled: enabled && !!collection?.id && !!pagesMetadata,
  });

  // Build collection structure with metadata first, then enhance with full content
  const structure: DisplayedCollectionSection[] | undefined = useMemo(() => {
    if (!collection) return undefined;

    const sections = collection.sections || [];
    const pagesToUse = fullPages || pagesMetadata; // Use full pages if available, fall back to metadata

    if (!pagesToUse) return undefined;

    return sections
      .filter(s => !s.archivedAt)
      .map(section => ({
        section: {
          ...section,
          pages: sortBy(
            pagesToUse.filter(p => p.sectionId === section.id),
            'orderNumber'
          ),
        },
      }));
  }, [collection, pagesMetadata, fullPages]);

  return {
    // Metadata for quick sidebar rendering
    pagesMetadata,
    // Full pages with content (may be undefined while loading)
    fullPages,
    // Current structure (metadata first, then enhanced with content)
    structure,
    // Loading states
    isMetadataLoading,
    isContentLoading,
    isLoading: isMetadataLoading,
    // Status helpers
    hasMetadata: !!pagesMetadata,
    hasFullContent: !!fullPages,
    hasStructure: !!structure,
  };
};
